import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DiscountTemplate } from '@/features/hosts/api/types/discount';
import { VISITOR_LANGUAGE, VisitorLanguage } from '@/features/visitors/api/types/visitor';
import { VisitFinishSetting } from '@/features/visits/api/types/visits';
import { addHours, max, set } from 'date-fns';

export const MAX_VISITORS = 50;

interface CreateVisitHostFormValues {
  id: number;
  name: string;
  allowInviteToParking: boolean;
  destinations: {
    id: number;
    name: string;
    description: string;
  }[];
  discountTemplates: DiscountTemplate[];
}

export interface CreateVisitVisitorFormValues {
  localId: string;
  firstName: string;
  lastName: string;
  email: string;
  isHandicapped: boolean;
  isPregnant: boolean;
  hasPet: boolean;
  hasAccessToParking: boolean;
  language: VisitorLanguage;
  description: string;
  discountTemplateId: number | null;
}

export interface CreateVisitFormValues {
  visitors: CreateVisitVisitorFormValues[];
  expectedAt: Date;
  finishesAt: Date;
  destinations: {
    id: number;
    name: string;
  }[];
  host: CreateVisitHostFormValues | null;
  categoryId: number;
  attachments: File[];
  description: string;
  visitFinishSetting: VisitFinishSetting;
}

export const defaultValues: CreateVisitFormValues = {
  visitors: [],
  expectedAt: new Date(),
  finishesAt: max([
    addHours(new Date(), 1),
    set(new Date(), { hours: 18, minutes: 0, seconds: 0, milliseconds: 0 }),
  ]),
  destinations: [],
  host: null,
  attachments: [],
  categoryId: 0,
  visitFinishSetting: VisitFinishSetting.TIME_ENDS,
  description: '',
};

export const useCreateVisitValidationSchema = () => {
  const { t } = useTranslation(['createVisit', 'validation']);

  const visitorSchema = z.object({
    localId: z.string().min(1),
    firstName: z.string().max(255).min(1, t('createVisit:validation.firstName')),
    lastName: z.string().max(255).min(1, t('createVisit:validation.lastName')),
    email: z.string().min(1, t('createVisit:validation.email')).email(t('validation:email')),
    language: z.enum([VISITOR_LANGUAGE.POLISH, VISITOR_LANGUAGE.ENGLISH]),
    description: z
      .string()
      .max(1000, t('validation:maxCharacters', { count: 1000 }))
      .optional(),
    isHandicapped: z.boolean(),
    isPregnant: z.boolean(),
    hasPet: z.boolean(),
    hasAccessToParking: z.boolean(),
    discountTemplateId: z.number().int().positive().nullable(),
  });

  const visitSchema = z
    .object({
      visitors: z
        .array(visitorSchema)
        .min(1, t('createVisit:validation.visitors'))
        .max(MAX_VISITORS, t('createVisit:validation.maxVisitors', { count: MAX_VISITORS })),
      categoryId: z.number().min(1, t('createVisit:validation.required')),
      expectedAt: z.date({ invalid_type_error: t('createVisit:validation.dateType') }),
      finishesAt: z.date({ invalid_type_error: t('createVisit:validation.dateType') }),
      destinations: z
        .array(
          z.object({
            id: z.number().min(1, t('createVisit:validation.destination')),
            name: z.string().min(1, t('createVisit:validation.required')),
          }),
        )
        .min(1, t('createVisit:validation.destination')),
      host: z
        .object({
          id: z.number().min(1, t('validation:required')),
          name: z.string().min(1, t('validation:required')),
          allowInviteToParking: z.boolean(),
          destinations: z.array(
            z.object({
              id: z.number().min(1, t('validation:required')),
              name: z.string().min(1, t('validation:required')),
              description: z.string(),
            }),
          ),
          discountTemplates: z.array(
            z.object({
              id: z.number().min(1, t('validation:required')),
              code: z.string().min(1, t('validation:required')),
              name: z.string().min(1, t('validation:required')),
              discountType: z.enum(['percentage', 'fixed_amount', 'full_coverage']).nullable(),
              discountValue: z.number().nullable(),
              currency: z.string().nullable(),
              timeLimitMinutes: z.number().int().positive().nullable(),
            }),
          ),
        })
        .nullable(),
      attachments: z.array(z.instanceof(File)).optional(),
      description: z
        .string()
        .max(1000, t('validation:maxCharacters', { count: 1000 }))
        .optional(),
      visitFinishSetting: z.nativeEnum(VisitFinishSetting),
    })
    .refine(data => data.finishesAt > data.expectedAt, {
      message: t('createVisit:validation.finishesAt'),
      path: ['finishesAt'],
    })
    .refine(data => data.finishesAt > new Date(), {
      message: t('createVisit:validation.finishesAtFuture'),
      path: ['finishesAt'],
    });

  return { visitSchema, visitorSchema };
};
